import { extractBackendMessage } from '@lib/api/extractBackendMessage';
import { isAuthErrorHandledByInterceptor } from '@lib/api/serverStateErrorHandler';
import type { TFunction } from 'i18next';

/**
 * The refusals `DELETE /audit/:id/documents/:docId` answers, each mapped to its
 * `documents.*` sentence. The route sends a BARE JSON STRING and no code
 * (`packages/api/controller/audit/auditDocument.controller.js`, `delete`), so the exact
 * sentence is the only thing to match on; a reworded one falls back to the server's text.
 */
const REFUSAL_KEYS: Record<string, string> = {
  // An import's source stays on file as its evidence (docs/API_INVARIANTS.md, #760).
  'Document is the source of a data-analysis import': 'delete_document_analysis_source',
  // Reachable with Delete live: the Trash list carries no backlinks, the route also refuses
  // a status step whose text merely contains the document's URL, and a row can be stale.
  'Document is used in active backlinks': 'delete_document_has_backlinks',
};

/**
 * The toast for a document delete that did not happen: a known refusal in the user's
 * language, else the server's own sentence, else a generic "could not be deleted".
 * `undefined` for a 401/403, which the axios interceptor answers with a logout.
 * `t` is the `documents` namespace.
 */
export function describeDocumentDeleteFailure(error: unknown, t: TFunction): string | undefined {
  if (isAuthErrorHandledByInterceptor(error)) return undefined;
  const message = extractBackendMessage(error);
  if (message === undefined) return t('delete_document_failed');
  const key = REFUSAL_KEYS[message];
  return key ? t(key) : message;
}
